import React, { useState } from "react";
import styles from "./DocumentUploadPopup.module.css";
import WebService from "../Services/WebService";

interface Props {
  open: boolean;
  onClose: () => void;
  taskId: number | null;      // Pass only the created Task ID
  onSuccess: () => void;
}

const DocumentUploadPopup: React.FC<Props> = ({ open, onClose, taskId, onSuccess }) => {
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setError(null);
    const f = e.target.files?.[0];
    if (!f) return;
    // Only allow pdf, docx, jpg, jpeg, png up to 10 MB
    if (![
      "application/pdf",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "image/png",
      "image/jpeg"
    ].includes(f.type)) {
      setError("Only PDF, DOCX, PNG, or JPG files allowed.");
      return;
    }
    if (f.size > 10 * 1024 * 1024) {
      setError("Max file size is 10 MB.");
      return;
    }
    setFile(f);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!file) {
      setError("Please select a file.");
      return;
    }
    if (!taskId) {
      setError("Task not found. Please try again.");
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("task_id", String(taskId));
      formData.append("comment", file.name);
      formData.append("image", file);

      const result: any = await WebService.postAPI({
        action: "/v1/door-management/comment",
        body: formData,
        isFormData: true
      });

      if (result && (result.status_code === 201 || result.status_code === 200)) {
        setFile(null);
        onSuccess();
        onClose();
      } else {
        setError(result?.message || "Failed to upload.");
      }
    } catch (err: any) {
      setError(typeof err === "string" ? err : "Error uploading");
    }
    setUploading(false);
  };

  if (!open) return null;

  return (
    <div className={styles.overlay}>
      <div className={styles.popupPanel}>
        <div className={styles.headerRow}>
          <span className={styles.title}>Upload Document</span>
          <button className={styles.closeBtn} onClick={onClose} type="button">×</button>
        </div>
        <form className={styles.form} onSubmit={handleSubmit}>
          <input
            type="file"
            accept=".pdf,.docx,.jpg,.jpeg,.png"
            onChange={handleFileChange}
            disabled={uploading}
          />
          {file && <div style={{ marginTop: 8, fontSize: 14 }}>{file.name}</div>}
          {error && <div style={{ color: "#e43e2d", marginTop: 8 }}>{error}</div>}
          <button
            type="submit"
            className={styles.createBtn}
            disabled={uploading || !file}
            style={{ marginTop: 16 }}
          >
            {uploading ? "Uploading..." : "Upload"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default DocumentUploadPopup;
